/*
 * he-cart-fixes.js  v4 (Happy Ears, 28 Oct 2026)
 *
 * v4 ADDS (28 Oct 2026, after the drawer flicker reproduced on a Chrome tab restored after an update):
 *   FIX 3 - never read a cached cart. Every cart read goes to Shopify with cache: 'no-store'.
 *   FIX 4 - re-sync a stale tab. On page load, and when a tab becomes visible again after >= 20 s,
 *           read the live cart; if the drawer shows different lines, redraw it before anything else.
 *           (Restored tabs - Chrome update/restart, mobile background tabs - show a snapshot whose
 *           line keys no longer exist.)
 *   FIX 5 - loop brake. Each free-gift add/remove redraws the drawer, and each redraw runs the gift
 *           check again. If the gift is changed 3 times within 10 s, gift checks pause for 8 s, the
 *           drawer is redrawn once from the live cart, then one fresh check runs. A second brake
 *           within 60 s stops automatic checks until the shopper next touches the cart.
 *   FIX 6 - only a line carrying the _free_gift property is "the gift". The Discovery Pack is both
 *           the gift and a product people buy; the Buy-X-get-Y discount can move the free unit onto a
 *           PAID Discovery Pack line (price 0, no property). The theme counted that line as a gift
 *           slot and could remove a pack the shopper bought, then re-check. Paid packs are now never
 *           treated as the gift.
 *
 * v3 (30 Sep 2026):
 * v3 CHANGE: the request queue is REMOVED. v1 locked quantity changes in the live cart; the queue
 *   (one request at a time) was the only part that could make a request wait on another, so one
 *   stuck request or any code awaiting the cart inside a queued call blocked every later change.
 *   Each call now goes straight to the theme's own CartApi, exactly as without this file; only the
 *   one-shot stale-key retry is added on top.
 *
 * v2 CHANGES (after v1 was rolled back from live on 29 Sep):
 *   - Grouped rows (one product split into several lines by the gift discount, shown as one row
 *     with data-line-keys) are left entirely to assets/cart-drawer-grouping.js, which already
 *     disables its buttons while a request runs and re-renders on failure.
 *   - Stale-key retry only for SINGLE-line requests. Multi-key cart/update calls pass through
 *     untouched: remapping several split lines could map two stale keys onto the same new line and
 *     set a wrong total.
 *
 * FIX 1 - lost clicks after the free gift is added/removed.
 *   The "Free Discovery Pack offer" (Buy X get Y) makes Shopify re-key cart lines every time the gift
 *   is added or removed. The drawer sends +/-/remove with the key it last rendered, so a click made
 *   during or right after a gift change hits a key that no longer exists; Shopify answers 400
 *   ("no valid id or line selector") and the click is silently lost.
 *   -> On a 400 for a line key we re-read the cart, find the same line (same variant + same properties, never
 *      the free-gift line) under its new key, and resend once.
 *
 * FIX 2 - fast repeat clicks collapse into one.
 *   Each +/- button carries a pre-rendered target (data-quantity = rendered qty +/- 1). Clicking
 *   three times before the drawer re-renders sends the same target three times.
 *   -> A capture-phase click handler (runs before the theme's own) computes the target from the
 *      last requested quantity for that line (kept for 3 s), rewrites the button's data-quantity,
 *      and updates the visible input straight away.
 *
 * Both fixes are additive wrappers. Removing this file (and its <script> tag) restores the old behaviour.
 */
(function () {
  if (window.__heCartFixes) return;
  window.__heCartFixes = true;

  var KEY_RE = /^\d+:[0-9a-f]{32}$/i;

  /* ---------------- FIX 1 ---------------- */
  function patchCartApi() {
    var TC = window.ThemeCart;
    var Api = TC && TC.CartApi;
    if (!Api || !TC.CartLineRules) return false;
    if (Api.__heFixed) return true;

    var origChange = Api.change;
    var origUpdate = Api.update;

    // v3: no queue - run immediately, exactly like the theme's own call.
    function enqueue(fn) {
      return fn();
    }

    function isGift(item) {
      return TC.CartLineRules.hasThemeFreeGiftMarker(item);
    }

    function propsSig(item) {
      return (TC.CartLineRules.propertiesEntries(item) || [])
        .filter(function (p) { return !/^_?free_gift$/i.test(String(p[0])); })
        .map(function (p) { return p[0] + '=' + p[1]; })
        .sort()
        .join('&');
    }

    async function remapKey(staleKey, cartBefore) {
      var variantId = String(staleKey).split(':')[0];
      var old = null;
      if (cartBefore && Array.isArray(cartBefore.items)) {
        old = cartBefore.items.find(function (i) { return String(i.key) === String(staleKey); }) || null;
      }
      var fresh = await Api.fetchFreshCart();
      var candidates = (fresh.items || []).filter(function (i) {
        return String(i.variant_id) === variantId && !isGift(i);
      });
      if (old) {
        var sig = propsSig(old);
        var sameProps = candidates.filter(function (i) { return propsSig(i) === sig; });
        if (sameProps.length) candidates = sameProps;
        var sameQty = candidates.filter(function (i) { return i.quantity === old.quantity; });
        if (sameQty.length) candidates = sameQty;
      }
      return candidates.length ? String(candidates[0].key) : null;
    }

    Api.change = function (body, signal) {
      return enqueue(async function () {
        var cartBefore = Api.cartCache;
        var res = await origChange.call(Api, body, signal);
        if (res && res.status === 400 && body && KEY_RE.test(String(body.id || ''))) {
          var newKey = await remapKey(body.id, cartBefore);
          if (newKey && newKey !== String(body.id)) {
            res = await origChange.call(Api, Object.assign({}, body, { id: newKey }), signal);
          }
        }
        return res;
      });
    };

    Api.update = function (body, signal) {
      return enqueue(async function () {
        var cartBefore = Api.cartCache;
        var res = await origUpdate.call(Api, body, signal);
        var u = body && body.updates;
        var singleKey = u && typeof u === 'object' && !Array.isArray(u) && Object.keys(u).length === 1;
        if (res && res.status === 400 && singleKey) {
          var remapped = {};
          var changed = false;
          for (var k in u) {
            if (!Object.prototype.hasOwnProperty.call(u, k)) continue;
            if (KEY_RE.test(k)) {
              var nk = await remapKey(k, cartBefore);
              if (nk) {
                remapped[nk] = u[k];
                if (nk !== k) changed = true;
                continue;
              }
            }
            remapped[k] = u[k];
          }
          if (changed) {
            res = await origUpdate.call(Api, Object.assign({}, body, { updates: remapped }), signal);
          }
        }
        return res;
      });
    };

    Api.__heFixed = true;
    return true;
  }

  if (!patchCartApi()) {
    var tries = 0;
    var timer = setInterval(function () {
      if (patchCartApi() || ++tries > 100) clearInterval(timer);
    }, 50);
  }

  /* ---------------- FIX 3 / 4 / 5 / 6 (v4) ---------------- */
  var brakeUntil = 0, brakeStrikes = [], giftMutations = [], userTouched = false, retryTimer = null;

  function now() { return Date.now(); }

  function patchV4() {
    var TC = window.ThemeCart;
    var Api = TC && TC.CartApi;
    var Ctrl = TC && TC.FreeGiftController;
    var Rules = TC && TC.FreeGiftRules;
    if (!Api || !Ctrl || !Rules || !Api.__heFixed) return false;
    if (Api.__heV4) return true;

    // FIX 3: live cart only.
    Api.fetchFreshCart = async function (signal) {
      var res = await fetch(Api.url('/cart.js'), { credentials: 'same-origin', cache: 'no-store', signal: signal });
      if (!res.ok) throw new Error('Cart fetch failed: ' + res.status);
      var cart = await res.json();
      Api.setCartCache(cart);
      return cart;
    };

    // FIX 6: only the _free_gift-marked line is the gift.
    Rules.lineQualifiesAsThemeFreeGiftSlot = function (item, options, variantIdStr, propertyKey, propertyValue) {
      return Rules.propertyMatch(item, propertyKey, propertyValue);
    };
    Rules.lineExcludedFromPaidMerch = function (item, options, variantIdStr, propertyKey, propertyValue) {
      return Rules.propertyMatch(item, propertyKey, propertyValue);
    };

    // FIX 5: loop brake.
    var P = Ctrl.prototype;
    var origSync = P.syncFreeGift;
    var origRefresh = P._refreshAfterFreeGiftMutation;

    P.syncFreeGift = function (opts) {
      if (now() < brakeUntil && !userTouched) {
        try { this._setSyncQueued(false); } catch (e) {}
        try { this._setFreeGiftSyncOverlayVisible(false); } catch (e) {}
        try { this._releaseFreeGiftReplaceContentGate(); } catch (e) {}
        return Promise.resolve();
      }
      return origSync.call(this, opts);
    };

    P._refreshAfterFreeGiftMutation = async function () {
      var t = now();
      giftMutations = giftMutations.filter(function (x) { return t - x < 10000; });
      giftMutations.push(t);
      if (giftMutations.length >= 3) {
        giftMutations = [];
        brakeStrikes = brakeStrikes.filter(function (x) { return t - x < 60000; });
        brakeStrikes.push(t);
        userTouched = false;
        brakeUntil = brakeStrikes.length >= 2 ? Infinity : t + 8000;
        Api.markCartCacheDirty();
        var self = this;
        await origRefresh.call(self); // one redraw from the live cart; its gift check is paused
        if (brakeUntil !== Infinity) {
          clearTimeout(retryTimer);
          retryTimer = setTimeout(function () {
            brakeUntil = 0;
            Api.markCartCacheDirty();
            try { self.syncFreeGift(); } catch (e) {}
          }, 8100);
        }
        return;
      }
      return origRefresh.call(this);
    };

    // A real shopper action in the cart lifts the brake.
    ['pointerdown', 'keydown'].forEach(function (ev) {
      document.addEventListener(ev, function (e) {
        var t = e.target;
        if (t && t.closest && t.closest('cart-drawer, main-cart')) {
          userTouched = true;
          if (brakeUntil === Infinity) brakeUntil = 0;
        }
      }, true);
    });

    Api.__heV4 = true;
    return true;
  }

  // FIX 4: re-sync stale tabs.
  function drawerKeys() {
    var d = document.querySelector('cart-drawer');
    if (!d) return null;
    return Array.prototype.map.call(d.querySelectorAll('[id^="cart-item__"]'), function (e) {
      return e.id.replace('cart-item__', '');
    }).sort().join('|');
  }

  var resyncing = false;
  async function resyncIfStale() {
    if (resyncing || window.__checkoutInProgress) return;
    var TC = window.ThemeCart, Api = TC && TC.CartApi, d = document.querySelector('cart-drawer');
    if (!Api || !d || typeof d.renderCart !== 'function') return;
    resyncing = true;
    try {
      Api.markCartCacheDirty();
      var cart = await Api.fetchFreshCart();
      var live = (cart.items || []).map(function (i) {
        return TC.CartLineRules.itemDomIdFromLineKey(i.key).replace('cart-item__', '');
      }).sort().join('|');
      var shown = drawerKeys();
      if (shown !== null && shown !== live) {
        await d.renderCart(false, false, false, null, false);
      }
    } catch (e) {
    } finally {
      resyncing = false;
    }
  }

  if (!patchV4()) {
    var tries4 = 0;
    var timer4 = setInterval(function () {
      if (patchV4() || ++tries4 > 200) clearInterval(timer4);
    }, 50);
  }

  window.addEventListener('load', function () { setTimeout(resyncIfStale, 1500); });
  var hiddenAt = 0;
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') { hiddenAt = now(); return; }
    if (hiddenAt && now() - hiddenAt >= 20000) resyncIfStale();
  });
  window.addEventListener('pageshow', function (e) { if (e.persisted) resyncIfStale(); });
  window.__heCartFixesVersion = 4;

  /* ---------------- FIX 2 ---------------- */
  var pending = {}; // line key -> { q: requested qty, t: timestamp }

  window.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-action="update-item-quantity"]');
    if (!btn) return;
    var selector = btn.closest('.cart-item__quantity-selector') || btn.parentElement;
    // Grouped (split) rows are handled by cart-drawer-grouping.js - leave them alone.
    if (selector && selector.hasAttribute('data-line-keys')) return;
    var row = btn.closest('.cart-item');
    if (row && row.hasAttribute('data-line-keys')) return;
    var input = selector && selector.querySelector('input[data-action="update-change-quantity"], input.cart-item__quantity');
    if (!input) return;

    if (!btn.hasAttribute('data-he-orig')) btn.setAttribute('data-he-orig', btn.getAttribute('data-quantity') || '');
    var rendered = parseInt(input.defaultValue || input.getAttribute('value'), 10);
    var orig = parseInt(btn.getAttribute('data-he-orig'), 10);
    if (!isFinite(rendered) || !isFinite(orig)) return;
    var delta = orig - rendered;           // +1 for plus, -1 for minus
    if (delta === 0) return;

    var lineId = btn.getAttribute('data-line-id') || input.getAttribute('data-line-id') || '';
    var p = pending[lineId];
    var base = (p && Date.now() - p.t < 3000) ? p.q : parseInt(input.value, 10);
    if (!isFinite(base)) base = rendered;
    var max = parseInt(selector.getAttribute('data-max-qty'), 10) || 999;
    var next = Math.max(0, Math.min(max, base + delta));

    pending[lineId] = { q: next, t: Date.now() };
    btn.setAttribute('data-quantity', String(next));
    input.value = String(next);
  }, true);
})();
